import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";

import { type SkillSummary } from "@/shared/domain/skills";
import { useSkillVersions } from "@/shared/domain/skills/hooks";

import { type SkillSelection } from "../buildRequestTemplate";

import styles from "./SkillRow.module.css";

interface SkillRowProps {
  selection: SkillSelection;
  /** The matching org-shared skill (``org`` or ``org-editable``), or
   *  ``undefined`` when the attached ``skill_id`` no longer resolves — the
   *  skill was deleted, or downgraded to private, since it was attached. */
  summary: SkillSummary | undefined;
  /** When false the row is read-only: the remove button is hidden (for
   *  parity with ``GoalCard``) and the surrounding ``<fieldset>`` disables
   *  the version selector. */
  isEditing: boolean;
  onChange: (next: SkillSelection) => void;
  onRemove: () => void;
}

/**
 * One attached skill: its name, the latest-version description for context,
 * and a version selector that pins an exact version or tracks "Latest".
 *
 * When ``summary`` is undefined the skill no longer resolves; the row
 * renders a remove-me warning instead of a selector so a stale agent can
 * be repaired without guessing which entry is broken.
 */
export function SkillRow({
  selection,
  summary,
  isEditing,
  onChange,
  onRemove,
}: SkillRowProps) {
  // No version fetch for an unresolved skill — there is nothing to pin, and
  // its versions endpoint would 404.
  const versionsQuery = useSkillVersions(
    summary !== undefined ? selection.skill_id : undefined,
  );

  if (summary === undefined) {
    return (
      <section className={`${styles.row} ${styles.unavailable}`}>
        {/* The icon tile uses the row's unavailable color via CSS so a
            broken skill reads as red end-to-end (left accent + tile). */}
        <div className={`${styles.iconTile} ${styles.iconTileUnavailable}`}>
          <AutoAwesomeIcon fontSize="small" />
        </div>
        <div className={styles.main}>
          <span className={styles.name}>{selection.skill_id}</span>
          <span className={styles.warning}>
            This skill is no longer available — remove it.
          </span>
        </div>
        {isEditing && (
          <button
            type="button"
            aria-label={`Remove unavailable skill ${selection.skill_id}`}
            className={styles.removeButton}
            onClick={onRemove}
          >
            <CloseIcon fontSize="small" />
          </button>
        )}
      </section>
    );
  }

  const latestVersion = summary.latest_version?.version ?? null;
  const fetchedVersions = (versionsQuery.data ?? []).map((v) => v.version);
  // The pinned version must stay selectable even before the versions fetch
  // settles (or if it was since deleted), so the <select> can always render
  // its current value rather than silently falling back to "Latest".
  const versionNumbers = Array.from(
    new Set(
      selection.version === null
        ? fetchedVersions
        : [...fetchedVersions, selection.version],
    ),
  ).sort((a, b) => b - a);

  const selectId = `agent-skill-version-${selection.skill_id}`;

  return (
    <section className={styles.row}>
      <div className={styles.iconTile}>
        <AutoAwesomeIcon fontSize="small" />
      </div>
      <div className={styles.main}>
        <span className={styles.name}>{summary.skill.name}</span>
        {summary.latest_version !== null && (
          <span className={styles.description}>
            {summary.latest_version.description}
          </span>
        )}
      </div>
      <div className={styles.versionControl}>
        <label className={styles.versionLabel} htmlFor={selectId}>
          Version
        </label>
        <select
          id={selectId}
          className={styles.versionSelect}
          value={selection.version === null ? "" : String(selection.version)}
          onChange={(e) =>
            onChange({
              ...selection,
              version: e.target.value === "" ? null : Number(e.target.value),
            })
          }
        >
          <option value="">
            {latestVersion === null ? "Latest" : `Latest (v${latestVersion})`}
          </option>
          {versionNumbers.map((version) => (
            <option key={version} value={String(version)}>
              v{version}
            </option>
          ))}
        </select>
      </div>
      {isEditing && (
        <button
          type="button"
          aria-label={`Remove skill ${summary.skill.name}`}
          className={styles.removeButton}
          onClick={onRemove}
        >
          <CloseIcon fontSize="small" />
        </button>
      )}
    </section>
  );
}
